import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, KeyRound, CheckCircle2, CircleDashed, Save, Palette, Receipt } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { brandVars, contrastRatio, contrastFg } from "../lib/brand";
import { useApp, useModule } from "../context/AppContext";
import { PageHeader, Loading, ErrorBox, Denied, Btn, BrandLogo, Pill } from "../components/common";
import { TenantScope } from "../components/AdminScope";

const inputCls = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm";
const SECRET_LABELS = { m360_api_key: "Globe m360 API Key", m360_secret_key: "Globe m360 Secret Key", google_maps_api_key: "Google Maps API Key", mailtrap_api_token: "MailTrap API Token" };
const HEX = /^#[0-9A-Fa-f]{6}$/;

const Field = ({ label, id, children, hint }) => (
  <div><label htmlFor={id} className="block text-xs font-medium text-slate-700 mb-1">{label}</label>{children}{hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}</div>
);

function ColorField({ label, id, value, onChange }) {
  const ok = HEX.test(value);
  return (
    <Field label={label} id={id} hint={ok ? `Text on this color: ${contrastFg(value) === "#FFFFFF" ? "white" : "dark"} · contrast ${contrastRatio(value, contrastFg(value)).toFixed(1)}:1` : "Use #RRGGBB format"}>
      <div className="flex gap-2">
        <input type="color" aria-label={`${label} picker`} value={ok ? value : "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-10 w-12 rounded border cursor-pointer" data-testid={`${id}-picker`} />
        <input id={id} value={value} onChange={(e) => onChange(e.target.value)} maxLength={7} className={`${inputCls} font-mono ${ok ? "" : "border-red-400"}`} data-testid={`${id}-hex-input`} />
      </div>
    </Field>
  );
}

function Preview({ form, branding }) {
  const vars = brandVars(form.primary_color, form.secondary_color);
  const b = { ...branding, lgu_name: form.lgu_display_name || "—", system_name: form.system_name || "Barangay Information Management System" };
  return (
    <div style={vars} className="space-y-4" data-testid="branding-preview">
      <p className="overline-label">Live preview (unsaved)</p>
      <div className="rounded-md border overflow-hidden">
        <div className="bg-brand p-5 flex items-center gap-3"><BrandLogo branding={b} size={44} testid="preview-login-logo" /><div><p className="font-display font-bold" data-testid="preview-lgu-name">{b.lgu_name}</p><p className="text-xs opacity-90">{b.system_name}</p></div></div>
        <div className="p-4 bg-white text-xs text-slate-500">Login screen brand panel</div>
      </div>
      <div className="rounded-md border overflow-hidden">
        <div className="bg-brand h-12 px-3 flex items-center gap-2"><BrandLogo branding={b} size={28} testid="preview-header-logo" /><p className="text-sm font-semibold truncate flex-1">{b.lgu_name} — {b.system_name}</p><span className="h-6 w-1 bg-brand2 rounded" /></div>
        <div className="flex bg-white">
          <div className="w-36 border-r p-2 space-y-1 text-xs"><div className="nav-active rounded-sm px-2 py-1">Dashboard</div><div className="px-2 py-1">Inhabitants</div><div className="px-2 py-1">Clearances</div></div>
          <div className="p-3 flex-1 space-y-2"><button type="button" className="btn-brand rounded px-3 py-1 text-xs">Primary button</button><button type="button" className="bg-brand2 rounded px-3 py-1 text-xs ml-2">Secondary</button><p className="text-xs text-brand underline">Sample link</p></div>
        </div>
      </div>
    </div>
  );
}

function Secrets({ s, onSaved }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async (clear = []) => {
    setBusy(true);
    try { onSaved((await api.put("/settings/secrets", { ...vals, clear })).data); setVals({}); toast.success("Credentials updated (values are never shown again)"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-md border bg-white p-5" data-testid="settings-secrets-card">
      <h2 className="font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-brand" />Integration credentials</h2>
      <p className="text-xs text-slate-500 mt-1 mb-4">Stored encrypted server-side. Saved values are never returned to the browser. Enter a new value only to replace/rotate.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {Object.entries(SECRET_LABELS).map(([k, label]) => (
          <div key={k}>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor={`secret-${k}`} className="text-xs font-medium">{label}</label>
              {s.secrets[k].configured
                ? <span className="flex items-center gap-1 text-[11px] text-emerald-700" data-testid={`secret-status-${k}`}><CheckCircle2 className="h-3 w-3" />Configured ••••</span>
                : <span className="flex items-center gap-1 text-[11px] text-slate-500" data-testid={`secret-status-${k}`}><CircleDashed className="h-3 w-3" />Not configured</span>}
            </div>
            <div className="flex gap-2">
              <input id={`secret-${k}`} type="password" autoComplete="off" placeholder={s.secrets[k].configured ? "Enter new value to rotate" : "Enter value"} value={vals[k] || ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} className={inputCls} data-testid={`secret-input-${k}`} />
              {s.secrets[k].configured && <Btn variant="outline" className="!px-2" disabled={busy} onClick={() => save([k])} aria-label={`Clear ${label}`} data-testid={`secret-clear-${k}`}><Trash2 className="h-4 w-4" /></Btn>}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex justify-end"><Btn disabled={busy || !Object.values(vals).some(Boolean)} onClick={() => save()} data-testid="save-secrets-btn"><Save className="h-4 w-4" />Save credentials</Btn></div>
    </section>
  );
}

function FeesCard({ s, onSaved }) {
  const [fees, setFees] = useState(() => Object.fromEntries(s.document_fees.map((d) => [d.key, String(d.fee)])));
  const [busy, setBusy] = useState(false);
  const invalid = Object.values(fees).some((v) => v === "" || isNaN(Number(v)) || Number(v) < 0 || Number(v) > 100000);
  const save = async () => {
    const changed = Object.fromEntries(s.document_fees.filter((d) => Number(fees[d.key]) !== d.fee).map((d) => [d.key, Number(fees[d.key])]));
    if (!Object.keys(changed).length) { toast.info("No fee changes to save."); return; }
    setBusy(true);
    try {
      onSaved((await api.put("/settings/fees", { fees: changed, version: s.version })).data);
      toast.success("Document fees saved. New requests will use these fees.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-md border bg-white p-5" data-testid="settings-fees-card">
      <h2 className="font-semibold flex items-center gap-2"><Receipt className="h-4 w-4 text-brand" />Document fee schedule (PHP)</h2>
      <p className="text-xs text-slate-500 mt-1 mb-4">Applies to this LGU only, for new clearances and online requests. Existing requests keep the fee they were filed with. Enter 0 for free documents.</p>
      <div className="divide-y rounded-md border">
        {s.document_fees.map((d, i) => (
          <div key={d.key} className="flex items-center justify-between gap-3 px-3 py-2">
            <label htmlFor={`fee-${i}`} className="text-sm">{d.key}{!d.configured && <span className="ml-2 text-[11px] text-amber-700">default placeholder</span>}</label>
            <div className="flex items-center gap-1"><span className="text-sm text-slate-500">₱</span>
              <input id={`fee-${i}`} type="number" min="0" max="100000" step="0.01" value={fees[d.key]} onChange={(e) => setFees({ ...fees, [d.key]: e.target.value })} className="w-28 rounded-md border border-slate-300 px-2 py-1.5 text-sm text-right font-mono" data-testid={`fee-input-${i}`} />
            </div>
          </div>
        ))}
      </div>
      {invalid && <p className="text-xs text-red-700 mt-2" data-testid="fees-error">Each fee must be a number from 0 to 100,000.</p>}
      <div className="mt-4 flex justify-end"><Btn disabled={busy || invalid} onClick={save} data-testid="save-fees-btn"><Save className="h-4 w-4" />{busy ? "Saving…" : "Save fees"}</Btn></div>
    </section>
  );
}

function LogoCard({ s, onSaved }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { onSaved((await api.post("/settings/logo", fd)).data); toast.success("Logo updated"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const remove = async () => { try { onSaved((await api.delete("/settings/logo")).data); toast.success("Logo removed — fallback seal will be shown"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex items-center gap-4" data-testid="settings-logo-card">
      <BrandLogo branding={s.branding} size={72} testid="settings-logo-preview" />
      <div className="space-y-2">
        <input ref={ref} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="logo-file-input" />
        <div className="flex gap-2">
          <Btn variant="outline" disabled={busy} onClick={() => ref.current.click()} data-testid="logo-upload-btn"><Upload className="h-4 w-4" />{busy ? "Validating…" : s.branding.logo_url ? "Replace logo" : "Upload logo"}</Btn>
          {s.branding.logo_url && <Btn variant="ghost" onClick={remove} data-testid="logo-remove-btn"><Trash2 className="h-4 w-4" />Remove</Btn>}
        </div>
        <p className="text-[11px] text-slate-500">PNG or JPEG, max {s.logo_limits.max_kb} KB, 32–2048 px. Content is verified and re-encoded; SVG is rejected.</p>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const allowed = useModule("settings");
  const [scope, setScope] = useState(null);
  if (!allowed) return <Denied text="System Settings is restricted to the Super Admin." />;
  return (
    <div>
      <TenantScope onChange={setScope} prefix="settings-scope" />
      {scope && <SettingsBody key={scope.id} />}
    </div>
  );
}

function SettingsBody() {
  const allowed = useModule("settings");
  const { reload } = useApp();
  const [s, setS] = useState(null);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isAllowed = !!allowed;
  const apply = (data) => { setS(data); setForm({ ...data }); reload(); };
  const applyPartial = (data) => { setS(data); setForm((f) => ({ ...f, version: data.version })); reload(); };
  useEffect(() => { if (isAllowed) api.get("/settings").then((r) => { setS(r.data); setForm({ ...r.data }); }).catch((e) => setErr(errMsg(e))); }, [isAllowed]);

  if (!allowed) return <Denied text="System Settings is restricted to the Super Admin." />;
  if (err) return <ErrorBox text={err} />;
  if (!form) return <Loading />;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));
  const save = async (e) => {
    e.preventDefault();
    if (!HEX.test(form.primary_color) || !HEX.test(form.secondary_color)) { toast.error("Colors must be #RRGGBB"); return; }
    setBusy(true);
    try { apply((await api.put("/settings", form)).data); toast.success("Settings saved and branding applied"); } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader crumbs={["Administration", "System Settings"]} title={`System Settings — ${s.tenant_context?.name || ""}`} subtitle={`${[s.tenant_context?.city, s.tenant_context?.province].filter(Boolean).join(", ")} · ${s.configured ? "Saved barangay configuration" : "Unconfigured: showing defaults until you save"}. Changes apply only to this barangay and are recorded in the audit trail (secrets excluded).`} />
      <div className="mb-5 flex flex-wrap gap-2" data-testid="integration-status">
        {Object.entries(s.integrations).map(([k, v]) => <Pill key={k} tone={v === "live" || v === "configured" ? "approved" : "escalated"} testid={`integration-${k}`}>{k}: {v}</Pill>)}
      </div>
      <div className="grid xl:grid-cols-[1.4fr_1fr] gap-6">
        <div className="space-y-6">
          <form onSubmit={save} className="rounded-md border bg-white p-5 space-y-5" data-testid="settings-branding-form">
            <h2 className="font-semibold flex items-center gap-2"><Palette className="h-4 w-4 text-brand" />LGU identity & theme</h2>
            <LogoCard s={s} onSaved={applyPartial} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="LGU display name *" id="lgu_display_name"><input id="lgu_display_name" required value={form.lgu_display_name || ""} onChange={set("lgu_display_name")} className={inputCls} data-testid="settings-lgu-name-input" /></Field>
              <Field label="LGU type / jurisdiction" id="lgu_type">
                <select id="lgu_type" value={form.lgu_type} onChange={set("lgu_type")} className={inputCls} data-testid="settings-lgu-type-select">
                  {["province", "city", "municipality", "barangay"].map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2"><Field label="Official address" id="address"><input id="address" value={form.address || ""} onChange={set("address")} className={inputCls} data-testid="settings-address-input" /></Field></div>
              <Field label="Official contact number" id="contact_number"><input id="contact_number" value={form.contact_number || ""} onChange={set("contact_number")} className={inputCls} data-testid="settings-contact-input" /></Field>
              <Field label="Official email" id="official_email"><input id="official_email" type="email" value={form.official_email || ""} onChange={set("official_email")} className={inputCls} data-testid="settings-email-input" /></Field>
              <div className="sm:col-span-2"><Field label="System name" id="system_name" hint="Default: Barangay Information Management System"><input id="system_name" value={form.system_name || ""} onChange={set("system_name")} className={inputCls} data-testid="settings-system-name-input" /></Field></div>
              <ColorField label="Primary color" id="settings-primary-color" value={form.primary_color} onChange={set("primary_color")} />
              <ColorField label="Secondary color" id="settings-secondary-color" value={form.secondary_color} onChange={set("secondary_color")} />
              <Field label="Globe m360 Short Code" id="m360_short_code"><input id="m360_short_code" value={form.m360_short_code || ""} onChange={set("m360_short_code")} className={inputCls} data-testid="settings-shortcode-input" /></Field>
              <Field label="Postal code (inherited by resident addresses)" id="postal_code"><input id="postal_code" maxLength={4} value={form.postal_code || ""} onChange={set("postal_code")} className={inputCls} data-testid="settings-postal-code-input" /></Field>
            </div>
            <div className="flex justify-end"><Btn type="submit" disabled={busy} data-testid="settings-save-btn"><Save className="h-4 w-4" />{busy ? "Saving…" : "Save settings"}</Btn></div>
          </form>
          <FeesCard s={s} onSaved={applyPartial} />
          <Secrets s={s} onSaved={applyPartial} />
        </div>
        <aside className="rounded-md border bg-white p-5 h-fit xl:sticky xl:top-20"><Preview form={form} branding={s.branding} /></aside>
      </div>
    </div>
  );
}
